import { NetworkStatus } from "@apollo/client";
import { useFocusEffect } from "expo-router";
import {
  ArrowRightLeft,
  Eye,
  FileCheck,
  FilePenLine,
  FileSearch,
  FileText,
  LogIn,
  LogOut,
  Minus,
  Pen,
  PlusIcon,
  RotateCcw,
  Share2,
  Trash2,
  User,
  UserPlus,
} from "lucide-react-native";
import React, { useCallback, useMemo, useState } from "react";
import { Platform } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";

import ActivityFeedItemContainer from "@/components/molecules/ActivityFeedItem/ActivityFeedItemContainer";
import ItemSeparator from "@/components/molecules/ItemSeparator";
import SkeletonListContainer from "@/components/organisms/SkeletonList";
import TabViewHeader from "@/components/organisms/TabViewHeader";

import RosterChartsHorizontalScroll from "@/components/organisms/RosterChartsHorizontalScroll";
import { Box } from "@/components/atoms/Box";
import { Divider } from "@/components/atoms/Divider";
import { FlashList } from "@/components/atoms/FlashList";
import { Heading } from "@/components/atoms/Heading";
import { RefreshControl } from "@/components/atoms/RefreshControl";
import { Spinner } from "@/components/atoms/Spinner";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import type { ListRenderItem } from "@/components/atoms/FlashList";
import { useCurrentUser } from "@/features/auth";
import {
  ActivityFeedFragment,
  useListActivitiesFeedQuery,
  useUserNotificationPreferencesQuery,
} from "@/generated/graphql";
import useActivityDeepLink from "@/hooks/useActivityDeepLink";
import useActivityPlayerNames from "@/hooks/useActivityPlayerNames";
import useMobileScreen from "@/hooks/useMobileScreen";
import defaultExtractKey from "@/utils/defaultExtractKey";
import { useDismissedActivityIds } from "@/utils/dismissedActivities";
import { enrichActivityItem } from "@/utils/enrichActivityItem";
import { getBodyContent } from "@/utils/handleActivityMetadata";
import { getUserDisplayName } from "@/utils/getUserDisplayName";
import { ifWeb } from "@/utils/platform";

/**
 * Feed container padding style.
 * design-system: dynamic-style waiver — data-driven value (popover-context padding)
 * @param isOpenOnPopover - Whether the feed is rendered inside a popover
 */
const feedContainerStyle = (isOpenOnPopover?: boolean) => ({
  flex: 1,
  padding: isOpenOnPopover ? 0 : 12,
  paddingBottom: 0,
});

export const activitiesConfigs: Record<string, React.ElementType> = {
  PLAYER_ADDED_TO_WATCHLIST: PlusIcon,
  PLAYER_REMOVED_FROM_WATCHLIST: Minus,
  PLAYER_RATING_UPDATED: Pen,
  PLAYER_ANALYSIS_UPDATED: FileSearch,
  SCOUT_REPORT_CREATED: FileSearch,
  SCOUT_REPORT_UPDATED: FilePenLine,
  SCOUT_REPORT_ARCHIVED: Trash2,
  SCOUT_REPORT_SHARED: Share2,
  CONTENT_SHARED: Share2,
  USER_JOINED_ORGANIZATION: User,
  USER_LEFT_ORGANIZATION: User,
  NOTE_CREATED: FileText,
  NOTE_SHARED: Share2,
  NOTE_VISIBILITY_CHANGED: Eye,
  KANBAN_BOARD_VISIBILITY_CHANGED: Eye,
  USER_SIGNED_IN: LogIn,
  USER_SIGNED_OUT: LogOut,
  MANUAL_PLAYER_CREATED: UserPlus,
  MANUAL_PLAYER_UPDATED: Pen,
  MANUAL_PLAYER_DELETED: Trash2,
  MANUAL_PLAYER_RESTORED: RotateCcw,
  MANUAL_PLAYER_DATA_TRANSFERRED: ArrowRightLeft,
  MATCH_REPORT_SUBMITTED: FileCheck,
};

/**
 * Semantic icon color by activity type category.
 * Blue for info/create, orange for updates, red for destructive, gray for auth/system.
 */
const ACTIVITY_ICON_COLORS: Record<string, string> = {
  PLAYER_ADDED_TO_WATCHLIST: "text-needs-semantic-info-600",
  PLAYER_REMOVED_FROM_WATCHLIST: "text-status-error",
  PLAYER_RATING_UPDATED: "text-status-warning",
  PLAYER_ANALYSIS_UPDATED: "text-needs-semantic-info-600",
  SCOUT_REPORT_CREATED: "text-needs-semantic-info-600",
  SCOUT_REPORT_UPDATED: "text-status-warning",
  SCOUT_REPORT_ARCHIVED: "text-status-error",
  SCOUT_REPORT_SHARED: "text-needs-semantic-info-600",
  CONTENT_SHARED: "text-needs-semantic-info-600",
  USER_JOINED_ORGANIZATION: "text-needs-semantic-success-600",
  USER_LEFT_ORGANIZATION: "text-status-error",
  NOTE_CREATED: "text-needs-semantic-info-600",
  NOTE_SHARED: "text-needs-semantic-info-600",
  NOTE_VISIBILITY_CHANGED: "text-status-warning",
  KANBAN_BOARD_VISIBILITY_CHANGED: "text-status-warning",
  USER_SIGNED_IN: "text-content-subtle",
  USER_SIGNED_OUT: "text-content-subtle",
  MANUAL_PLAYER_CREATED: "text-needs-semantic-success-600",
  MANUAL_PLAYER_UPDATED: "text-status-warning",
  MANUAL_PLAYER_DELETED: "text-status-error",
  MANUAL_PLAYER_RESTORED: "text-needs-semantic-success-600",
  MANUAL_PLAYER_DATA_TRANSFERRED: "text-status-warning",
  MATCH_REPORT_SUBMITTED: "text-needs-semantic-info-600",
};

/** Default icon color when activity type is not mapped */
const DEFAULT_ICON_COLOR = "text-needs-semantic-info-600";

/**
 * Props for ActivityFeedContainer component
 */
interface IActivityFeedProps {
  isOpenOnPopover?: boolean;
  isShowHeader?: boolean;
  onClosePopover?: () => void;
  isDark?: boolean;
}

/**
 * Container component for activity feed with real-time updates, filtering, and pagination
 * @param root0 - Component props
 * @param root0.isOpenOnPopover - Whether feed is displayed inside a popover menu
 * @param root0.isShowHeader - Whether to show header with charts and "Recent Activity" title
 * @param root0.onClosePopover - Callback invoked when closing the popover
 * @param root0.isDark - Whether to use dark theme styling
 */
const ActivityFeedContainer = ({
  isOpenOnPopover,
  isShowHeader = true,
  onClosePopover,
  isDark,
}: IActivityFeedProps) => {
  const isSmallScreen = useMobileScreen();
  const [isRefetchingOnFocus, setIsRefetchingOnFocus] = useState(false);
  const currentUser = useCurrentUser();

  const { data, loading, fetchMore, refetch, networkStatus, error } =
    useListActivitiesFeedQuery({
      variables: {
        first: 15,
      },
      notifyOnNetworkStatusChange: true,
      errorPolicy: "all",
    });

  // SE-3101: Fetch user notification preferences to filter activities client-side
  // Note: This is a frontend workaround. The backend query
  // doesn't filter by user preferences, so we filter on the client.
  const { data: preferencesData } = useUserNotificationPreferencesQuery();

  // Build a Set of disabled activity type codes for efficient lookup
  const disabledActivityCodes = useMemo(() => {
    return new Set(
      preferencesData?.userNotificationPreferences
        .filter(pref => !pref.enabled)
        .map(pref => pref.activityType.code) ?? []
    );
  }, [preferencesData]);

  // SE-4096: Shared dismissed IDs survive poll responses, tab switches,
  // and "Mark all as read" from the bell popover
  const dismissedIds = useDismissedActivityIds();

  const activityData = useMemo(() => {
    const activities = data?.listMyActivities?.edges?.map(edge => edge.node);

    // Filter out activities that the user has disabled in notification settings.
    // SE-3854: When displayed in the bell popover, also filter out read items
    // so marking all as read clears the list instantly (1:1 with What's New).
    // SE-4096: Also filter out locally dismissed activities that the server
    // may not have persisted yet.
    return activities?.filter(
      activity =>
        !disabledActivityCodes.has(activity.activity.activityType.code) &&
        !dismissedIds.has(activity.activityId) &&
        (!isOpenOnPopover || !activity.isRead)
    );
  }, [
    data?.listMyActivities?.edges,
    disabledActivityCodes,
    dismissedIds,
    isOpenOnPopover,
  ]);

  // SE-3780: Build userId-to-name map from inline user data on each activity,
  // eliminating the separate N+1 query that fetched all org users
  const userIdToNameMap = useMemo(
    () =>
      (activityData ?? []).reduce(
        (acc, node) =>
          // SE-4894: system-generated activities have a null userId — skip them
          node.activity.user && node.activity.userId
            ? {
                ...acc,
                [node.activity.userId]: getUserDisplayName(node.activity.user),
              }
            : acc,
        {} as Record<string, string>
      ),
    [activityData]
  );

  // SE-3778: Resolve full player names to prevent truncation in activity titles
  // SE-3990: noteIdToPlayerIdMap resolves playerIds for note activities missing metadata.playerId
  const { playerNames, noteIdToPlayerIdMap } =
    useActivityPlayerNames(activityData);

  const { handleActivityTapped } = useActivityDeepLink();
  const isRefreshing = networkStatus === NetworkStatus.refetch;

  const handleEndReached = useCallback(() => {
    // SE-3854: Popover filters out read items client-side, so most fetched pages
    // yield zero visible items. This causes FlashList to fire onEndReached in a
    // loop (list never fills the viewport). Disable pagination in popover mode;
    // the initial 15-item fetch provides enough recent unread items.
    if (isOpenOnPopover) return;

    const { hasNextPage, endCursor } = data?.listMyActivities.pageInfo ?? {};
    if (!loading && hasNextPage && endCursor) {
      fetchMore({
        variables: {
          first: 10,
          after: endCursor,
        },
        updateQuery(previousQueryResult, { fetchMoreResult }) {
          if (!fetchMoreResult) return previousQueryResult;

          if (
            previousQueryResult.listMyActivities.pageInfo.endCursor ===
            fetchMoreResult.listMyActivities.pageInfo.endCursor
          ) {
            return previousQueryResult;
          }

          return {
            listMyActivities: {
              ...previousQueryResult.listMyActivities,
              edges: [
                ...previousQueryResult.listMyActivities.edges,
                ...fetchMoreResult.listMyActivities.edges,
              ],
              pageInfo: fetchMoreResult.listMyActivities.pageInfo,
            },
          };
        },
      });
    }
  }, [data?.listMyActivities.pageInfo, loading, fetchMore, isOpenOnPopover]);

  const handleRefresh = useCallback(() => refetch(), [refetch]);

  const onPressActivity = useCallback(
    (item: ActivityFeedFragment) => {
      onClosePopover?.();
      handleActivityTapped(item);
    },
    [onClosePopover, handleActivityTapped]
  );

  const renderItem: ListRenderItem<ActivityFeedFragment> = useCallback(
    ({ item }) => {
      const { activity } = item ?? {};
      const activityType =
        activity.activityType.code || "USER_JOINED_ORGANIZATION";
      const activityIcon = activitiesConfigs?.[activityType];
      const bodyContent = getBodyContent(
        activityType,
        activity.metadata,
        activity.body,
        activity.user?.displayName
      );

      const metadata = activity.metadata as
        | Record<string, unknown>
        | null
        | undefined;

      const { playerId, enhancedTitle } = enrichActivityItem(
        {
          activityType,
          metadata,
          userId: activity.userId,
          targetId: activity.targetId,
          title: activity.title,
          notificationTitle: activity.notificationTitle,
          body: activity.body,
          notificationBody: activity.notificationBody,
        },
        {
          userIdToNameMap,
          playerNames,
          noteIdToPlayerIdMap,
          currentUserId: currentUser?.id,
        }
      );

      const iconColor =
        ACTIVITY_ICON_COLORS[activityType] ?? DEFAULT_ICON_COLOR;

      return (
        <ActivityFeedItemContainer
          activityItem={item}
          activityIcon={activityIcon}
          iconColor={iconColor}
          onPress={onPressActivity}
          bodyContent={bodyContent}
          enhancedTitle={enhancedTitle}
          playerId={playerId}
          isSmallScreen={isSmallScreen}
          isDark={isDark ?? true}
        />
      );
    },
    [
      onPressActivity,
      isSmallScreen,
      isDark,
      userIdToNameMap,
      currentUser,
      playerNames,
      noteIdToPlayerIdMap,
    ]
  );

  const renderListEmptyComponent = useCallback(
    () =>
      !loading && activityData?.length === 0 ? (
        <Stack className="items-center text-center">
          <Text variant="title" className="text-content-secondary">
            No activities
          </Text>
        </Stack>
      ) : (
        loading && <SkeletonListContainer type="activity-feed" />
      ),
    [activityData?.length, loading]
  );

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && activityData?.length ? (
        <Spinner size="large" color={isDark ? "white" : "#666666"} />
      ) : null,
    [loading, isRefreshing, activityData, isDark]
  );

  const renderItemSeparator = useCallback(
    () => (
      <Box className={ifWeb("py-5", "py-3")}>
        <Divider className="bg-outline-subtle" />
      </Box>
    ),
    []
  );

  const renderListHeaderComponent = useCallback(
    () =>
      isShowHeader ? (
        <>
          {/* Roster Charts Section - shown on small screens only */}
          <RosterChartsHorizontalScroll />

          {/* Recent Activity Section Header */}
          <Stack className="gap-2 pb-3">
            <Heading variant="title">Recent Activity</Heading>
          </Stack>
        </>
      ) : (
        <Box />
      ),
    [isShowHeader]
  );

  useFocusEffect(
    useCallback(() => {
      setIsRefetchingOnFocus(true);
      refetch().finally(() => {
        setIsRefetchingOnFocus(false);
      });
    }, [refetch])
  );

  // Handle error state to prevent app crashes (checked after all hooks)
  if (error && !data) {
    return (
      <SafeAreaView
        edges={Platform.OS === "web" ? ["bottom"] : []}
        // eslint-disable-next-line no-restricted-syntax -- safe-area primitive, no SafeAreaView atom; data-driven popover padding
        style={feedContainerStyle(isOpenOnPopover)}
      >
        {isSmallScreen && <TabViewHeader title="Home" />}
        <Stack className="flex-1 items-center justify-center gap-4 p-6">
          <Text variant="title" className="text-center text-content-secondary">
            Unable to load activities
          </Text>
          <Text variant="body" className="text-center text-content-muted">
            There was an error loading your activity feed. Please try again
            later.
          </Text>
        </Stack>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView
      edges={Platform.OS === "web" ? ["bottom"] : []}
      // eslint-disable-next-line no-restricted-syntax -- safe-area primitive, no SafeAreaView atom; data-driven popover padding
      style={feedContainerStyle(isOpenOnPopover)}
    >
      {/* Header with logo and navigation - only on small screens */}
      {isSmallScreen && <TabViewHeader title="Home" />}

      <FlashList
        className="w-full self-center"
        contentContainerClassName="flex-grow py-3"
        data={activityData}
        showsVerticalScrollIndicator={false}
        refreshing={
          networkStatus === NetworkStatus.refetch && !isRefetchingOnFocus
        }
        scrollEventThrottle={16}
        onEndReachedThreshold={0.01}
        keyExtractor={defaultExtractKey}
        ListEmptyComponent={renderListEmptyComponent}
        ListFooterComponent={renderListFooterComponent}
        ListHeaderComponent={renderListHeaderComponent}
        ItemSeparatorComponent={
          isSmallScreen ? renderItemSeparator : ItemSeparator
        }
        renderItem={renderItem}
        onEndReached={handleEndReached}
        onRefresh={handleRefresh}
        refreshControl={
          <RefreshControl
            refreshing={
              networkStatus === NetworkStatus.refetch && !isRefetchingOnFocus
            }
            onRefresh={handleRefresh}
            colors={[isDark ? "white" : "#666666"]}
            tintColor={isDark ? "white" : "#666666"}
          />
        }
      />
    </SafeAreaView>
  );
};

export default ActivityFeedContainer;
